import React, { useState, useEffect, useMemo } from 'react';
import { useAdminApiService } from '../../hooks/useAdminApiService';
import ReconciliationPanel from './ReconciliationPanel';
import LiveDataPanel from './LiveDataPanel';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title as ChartTitle,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { Line } from 'react-chartjs-2';
import './AdminComponents.css';

interface TradingStrategyReport {
  [key: string]: any;
}

interface TradingStrategyReportsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Register Chart.js components for this modal (used by detailed transactions chart)
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ChartTitle,
  ChartTooltip,
  ChartLegend,
  zoomPlugin
);

const TradingStrategyReportsModal: React.FC<TradingStrategyReportsModalProps> = ({ isOpen, onClose }) => {
  const adminApiService = useAdminApiService();
  const [activeTab, setActiveTab] = useState<'summary' | 'detailed' | 'detailed-users' | 'pnl' | 'recent' | 'comparison' | 'volatility' | 'reconciliation' | 'live-data'>('summary');
  const [selectedStrategyId, setSelectedStrategyId] = useState<number | null>(null);
  const [marketType, setMarketType] = useState<string>('FUTURES');
  const [recentDays, setRecentDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<TradingStrategyReport[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [strategyFilter, setStrategyFilter] = useState<string>('all');
  const [showPnlExplanation, setShowPnlExplanation] = useState(false);
  
  // Reset page, sort, and filter when tab changes
  useEffect(() => {
    setCurrentPage(1);
    setSortColumn(null);
    setSortDirection('asc');
    setStrategyFilter('all');
  }, [activeTab]);

  useEffect(() => {
    if (isOpen) {
      // On mobile, scroll to top when modal opens
      if (window.innerWidth <= 768) {
        // Function to perform all scroll operations
        const performScroll = () => {
          // Scroll window and document
          window.scrollTo(0, 0);
          if (document.documentElement) {
            document.documentElement.scrollTop = 0;
          }
          if (document.body) {
            document.body.scrollTop = 0;
          }
          
          // Scroll the admin-content container (this is the scrollable container on mobile)
          const adminContent = document.querySelector('.admin-content') as HTMLElement;
          if (adminContent) {
            adminContent.scrollTop = 0;
            adminContent.scrollTo(0, 0);
          }
          // Also try scrolling the admin-main if it exists
          const adminMain = document.querySelector('.admin-main') as HTMLElement;
          if (adminMain) {
            adminMain.scrollTop = 0;
          }
          // Try scrolling admin-content-body as well
          const adminContentBody = document.querySelector('.admin-content-body') as HTMLElement;
          if (adminContentBody) {
            adminContentBody.scrollTop = 0;
          }
        };
        
        // Perform scroll immediately
        performScroll();
        
        // Also perform scroll after render using double RAF to ensure DOM is ready
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            performScroll();
          });
        });
        
        // Prevent body scroll while modal is open
        document.body.classList.add('modal-open');
      }
      
      loadData();
      setCurrentPage(1); // Reset to first page when tab changes
    } else {
      // Restore body scroll when modal closes
      if (window.innerWidth <= 768) {
        document.body.classList.remove('modal-open');
      }
    }
    
    // Cleanup: restore body scroll on unmount
    return () => {
      if (window.innerWidth <= 768) {
        document.body.classList.remove('modal-open');
      }
    };
  }, [isOpen, activeTab, recentDays, marketType]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      let response;
      switch (activeTab) {
        case 'summary':
          response = await adminApiService.getTradingStrategyPnlSummary();
          break;
        case 'detailed':
          response = await adminApiService.getTradingStrategyDetailedTransactions();
          break;
        case 'detailed-users':
          response = await adminApiService.getTradingStrategyDetailedTransactionsWithUsers();
          break;
        case 'pnl':
          response = await adminApiService.getTradingStrategyPnlSummary();
          break;
        case 'recent':
          response = await adminApiService.getTradingStrategyRecentTransactions(recentDays);
          break;
        case 'comparison':
          response = await adminApiService.getTradingStrategyComparison();
          break;
        case 'volatility':
          response = await adminApiService.getTradingStrategyVolatilityData(marketType);
          break;
        case 'reconciliation':
          // Reconciliation tab doesn't load data here - it's handled by ReconciliationPanel
          return;
        case 'live-data':
          // Live Data tab doesn't load data here - it's handled by LiveDataPanel
          return;
        default:
          response = await adminApiService.getTradingStrategyPnlSummary();
      }

      if (response.success && response.data) {
        setData(response.data);
      } else {
        setError(response.error || 'Failed to load data');
      }
    } catch (err: any) {
      // Handle 401 errors gracefully without redirecting
      if (err.response?.status === 401 || err.message?.includes('401') || err.message?.includes('Unauthorized')) {
        setError('You do not have permission to view trading strategy reports. Please contact your administrator.');
      } else {
        setError(err.message || 'An error occurred while loading data');
      }
      console.error('Error loading report data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async (exportFunction: () => Promise<Blob>, filename: string) => {
    try {
      setExporting(`csv-${activeTab}`);
      const blob = await exportFunction();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError(err.message || 'Failed to export CSV');
    } finally {
      setExporting(null);
    }
  };

  const handleExportPDF = async (exportFunction: () => Promise<Blob>, filename: string) => {
    try {
      setExporting(`pdf-${activeTab}`);
      const blob = await exportFunction();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError(err.message || 'Failed to export PDF');
    } finally {
      setExporting(null);
    }
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'number') {
      // Format large numbers with commas
      if (Math.abs(value) >= 1000) {
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(value);
      }
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 8
      }).format(value);
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (typeof value === 'string' && value.includes('T') && value.includes('Z')) {
      // Try to format as date
      try {
        const date = new Date(value);
        return date.toLocaleString();
      } catch {
        return value;
      }
    }
    return String(value);
  };

  const getTableHeaders = (): string[] => {
    if (data.length === 0) return [];
    return Object.keys(data[0]);
  };

  // Sorting function
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // Toggle direction if clicking same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Get unique strategy names from data
  const uniqueStrategyNames = useMemo(() => {
    const names = new Set<string>();
    data.forEach((row) => {
      // Try different possible field names for strategy name
      const strategyName = row.strategy_name || row.strategyName || row.name || row.strategy || '';
      if (strategyName && strategyName !== '') {
        names.add(strategyName);
      }
    });
    return Array.from(names).sort();
  }, [data]);

  // Filter data by strategy name
  const filteredData = useMemo(() => {
    if (strategyFilter === 'all' || !strategyFilter) {
      return data;
    }
    return data.filter((row) => {
      const strategyName = row.strategy_name || row.strategyName || row.name || row.strategy || '';
      return strategyName === strategyFilter;
    });
  }, [data, strategyFilter]);

  // Sort data (after filtering)
  const sortedData = useMemo(() => {
    if (!sortColumn || filteredData.length === 0) {
      return filteredData;
    }

    return [...filteredData].sort((a, b) => {
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Handle numbers
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle dates (try to parse as date)
      const aDate = new Date(aValue);
      const bDate = new Date(bValue);
      if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
        return sortDirection === 'asc' 
          ? aDate.getTime() - bDate.getTime()
          : bDate.getTime() - aDate.getTime();
      }

      // Handle strings
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      if (sortDirection === 'asc') {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });
  }, [filteredData, sortColumn, sortDirection]);

  // Pagination calculations (after sorting)
  const totalPages = Math.ceil(sortedData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = sortedData.slice(startIndex, endIndex);

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const getExportFilename = (format: 'csv' | 'pdf'): string => {
    const date = new Date().toISOString().split('T')[0];
    const tabNames: Record<string, string> = {
      summary: 'summary',
      detailed: 'detailed-transactions',
      'detailed-users': 'detailed-transactions-with-users',
      pnl: 'pnl-summary',
      recent: 'recent-transactions',
      comparison: 'trading-comparison'
    };
    return `trading-strategy-${tabNames[activeTab]}-${date}.${format}`;
  };

  // Calculate totals for fees, P&L, and net profit/loss
  const calculateTotals = () => {
    let totalFees = 0;
    let totalPnl = 0;
    let unrealizedPnl = 0;
    let totalPnlWithUnrealized = 0;
    let activePositions = 0;

    sortedData.forEach((row) => {
      // Look for fee-related fields (try multiple possible field names)
      const fees = row.fees || row.fee || row.total_fees || row.totalFees || 0;
      totalFees += typeof fees === 'number' ? fees : parseFloat(fees) || 0;

      // Look for P&L related fields (try multiple possible field names)
      const pnl = row.pnl || row.profit_loss || row.profitLoss || row.total_pnl || row.totalPnl || 0;
      totalPnl += typeof pnl === 'number' ? pnl : parseFloat(pnl) || 0;

      // NEW: Look for unrealized P&L from open positions
      const unrealized = row.unrealized_pnl || row.unrealizedPnl || 0;
      unrealizedPnl += typeof unrealized === 'number' ? unrealized : parseFloat(unrealized) || 0;

      // NEW: Total P&L including unrealized
      const pnlWithUnrealized = row.total_pnl_with_unrealized || row.totalPnlWithUnrealized || 0;
      totalPnlWithUnrealized += typeof pnlWithUnrealized === 'number' ? pnlWithUnrealized : parseFloat(pnlWithUnrealized) || 0;

      // NEW: Count active positions
      const active = row.active_positions || row.activePositions || 0;
      activePositions += typeof active === 'number' ? active : parseInt(active) || 0;
    });

    const netProfitLoss = totalPnl - totalFees;
    const netProfitLossWithUnrealized = totalPnlWithUnrealized - totalFees;

    return { 
      totalFees, 
      totalPnl, 
      unrealizedPnl, 
      totalPnlWithUnrealized, 
      netProfitLoss,
      netProfitLossWithUnrealized,
      activePositions
    };
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const isMobile = window.innerWidth <= 768;

  // ===== Detailed Transactions - Entry vs Exit Price Chart Data =====
  const detailedPriceChart = useMemo(() => {
    if (activeTab !== 'detailed') return null;

    // Use filtered (and sorted) data for the current strategy selection
    const rows = sortedData
      .filter((row: any) => row.entry_price != null || row.exit_price != null);

    if (rows.length === 0) return null;

    // Sort by transaction date ascending for a time-based chart
    const parseDate = (row: any) => {
      const raw =
        row.transaction_date ||
        row.transactionDate ||
        row.created_at ||
        row.last_updated ||
        row.updated_at;
      return raw ? new Date(raw) : new Date();
    };

    const sorted = [...rows].sort((a, b) => +parseDate(a) - +parseDate(b));

    const labels = sorted.map((row: any) => {
      const d = parseDate(row);
      return d.toLocaleString('en-GB', {
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    });

    const entryPrices = sorted.map((row: any) => {
      const v = row.entry_price ?? row.entryPrice;
      const num = typeof v === 'number' ? v : parseFloat(v);
      return isNaN(num) ? null : num;
    });

    const exitPrices = sorted.map((row: any) => {
      const v = row.exit_price ?? row.exitPrice;
      const num = typeof v === 'number' ? v : parseFloat(v);
      return isNaN(num) ? null : num;
    });

    const symbols = sorted.map((row: any) => row.symbol || row.pair || '');

    const data = {
      labels,
      datasets: [
        {
          label: 'Entry Price',
          data: entryPrices,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.15)',
          fill: false,
          spanGaps: true,
          tension: 0.3,
        },
        {
          label: 'Exit Price',
          data: exitPrices,
          borderColor: 'rgb(234, 88, 12)',
          backgroundColor: 'rgba(234, 88, 12, 0.15)',
          fill: false,
          spanGaps: true,
          tension: 0.3,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index' as const,
        intersect: false,
      },
      plugins: {
        legend: {
          position: 'top' as const,
        },
        title: {
          display: true,
          text: 'Detailed Transactions – Entry vs Exit Prices',
          font: {
            size: 14,
            weight: 'bold' as const,
          },
        },
        tooltip: {
          callbacks: {
            title: (items: any[]) => {
              const idx = items[0].dataIndex;
              const symbol = symbols[idx] || '';
              return symbol ? `${symbol} – ${items[0].label}` : items[0].label;
            },
            label: (context: any) => {
              const value = context.parsed.y;
              if (value == null) return '';
              return `${context.dataset.label}: ${value}`;
            },
          },
        },
        zoom: {
          zoom: {
            wheel: {
              enabled: true,
            },
            pinch: {
              enabled: true,
            },
            mode: 'x' as const,
          },
          pan: {
            enabled: true,
            mode: 'x' as const,
          },
        },
      },
      scales: {
        y: {
          title: {
            display: true,
            text: 'Price',
          },
          ticks: {
            callback: (value: any) => {
              return Number(value).toFixed(6).replace(/\.?0+$/, '');
            },
          },
        },
        x: {
          title: {
            display: true,
            text: 'Transaction Time',
          },
        },
      },
    };

    return { data, options };
  }, [activeTab, sortedData]);

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 10000 }}>
      <div 
        className="modal-content reports-modal" 
        onClick={(e) => e.stopPropagation()} 
        style={{ 
          width: isMobile ? '100vw' : '98vw', 
          maxWidth: isMobile ? '100vw' : '1600px',
          height: isMobile ? '100vh' : 'auto',
          maxHeight: isMobile ? '100vh' : '90vh',
          margin: isMobile ? '0' : 'auto',
          borderRadius: isMobile ? '0' : '12px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: isMobile ? 'fixed' : 'relative',
          top: isMobile ? '0' : 'auto',
          left: isMobile ? '0' : 'auto',
          right: isMobile ? '0' : 'auto',
          bottom: isMobile ? '0' : 'auto',
          zIndex: isMobile ? 10001 : 'auto'
        }}
      >
        <div className="modal-header" style={{ 
          padding: isMobile ? '12px' : '24px',
          position: isMobile ? 'relative' : 'sticky',
          top: isMobile ? 'auto' : 0,
          backgroundColor: 'white',
          zIndex: 100,
          borderBottom: '1px solid #e5e7eb',
          flexShrink: 0
        }}>
          <h2 className="modal-title" style={{ fontSize: isMobile ? '18px' : '24px', margin: 0 }}>
            Trading Strategy Reports
          </h2>
          <button 
            className="modal-close" 
            onClick={onClose}
            style={{
              fontSize: isMobile ? '32px' : '28px',
              padding: isMobile ? '8px 12px' : '8px 12px',
              minWidth: isMobile ? '48px' : 'auto',
              minHeight: isMobile ? '48px' : 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              backgroundColor: 'transparent',
              border: 'none',
              color: '#333',
              fontWeight: 'bold',
              lineHeight: '1',
              flexShrink: 0
            }}
          >
            ×
          </button>
        </div>

        <div className="modal-body" style={{ 
          padding: isMobile ? '12px' : '24px',
          paddingBottom: isMobile ? '100px' : '24px', // Extra padding for bottom nav (80px) + safe area
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          minHeight: 0,
          maxHeight: 'none',
          marginBottom: 0
        }}>
          {/* Tabs */}
          <div className="reports-tabs" style={{ 
            flexWrap: 'wrap', 
            gap: isMobile ? '2px' : '4px',
            marginBottom: isMobile ? '12px' : '16px',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch'
          }}>
            <button
              className={`tab-button ${activeTab === 'summary' ? 'active' : ''}`}
              onClick={() => setActiveTab('summary')}
              style={{
                fontSize: isMobile ? '11px' : '14px',
                padding: isMobile ? '8px 12px' : '12px 24px',
                flex: isMobile ? '1 1 calc(50% - 2px)' : 'none',
                minWidth: isMobile ? '0' : 'auto'
              }}
            >
              Summary
            </button>
            <button
              className={`tab-button ${activeTab === 'detailed' ? 'active' : ''}`}
              onClick={() => setActiveTab('detailed')}
              style={{
                fontSize: isMobile ? '11px' : '14px',
                padding: isMobile ? '8px 12px' : '12px 24px',
                flex: isMobile ? '1 1 calc(50% - 2px)' : 'none',
                minWidth: isMobile ? '0' : 'auto'
              }}
            >
              {isMobile ? 'Detailed' : 'Detailed Transactions'}
            </button>
            <button
              className={`tab-button ${activeTab === 'detailed-users' ? 'active' : ''}`}
              onClick={() => setActiveTab('detailed-users')}
              style={{
                fontSize: isMobile ? '11px' : '14px',
                padding: isMobile ? '8px 12px' : '12px 24px',
                flex: isMobile ? '1 1 calc(50% - 2px)' : 'none',
                minWidth: isMobile ? '0' : 'auto'
              }}
            >
              {isMobile ? 'User Details' : 'With User Details'}
            </button>
            <button
              className={`tab-button ${activeTab === 'pnl' ? 'active' : ''}`}
              onClick={() => setActiveTab('pnl')}
              style={{
                fontSize: isMobile ? '11px' : '14px',
                padding: isMobile ? '8px 12px' : '12px 24px',
                flex: isMobile ? '1 1 calc(50% - 2px)' : 'none',
                minWidth: isMobile ? '0' : 'auto'
              }}
            >
              P&L Summary
            </button>
            <button
              className={`tab-button ${activeTab === 'recent' ? 'active' : ''}`}
              onClick={() => setActiveTab('recent')}
              style={{
                fontSize: isMobile ? '11px' : '14px',
                padding: isMobile ? '8px 12px' : '12px 24px',
                flex: isMobile ? '1 1 calc(50% - 2px)' : 'none',
                minWidth: isMobile ? '0' : 'auto'
              }}
            >
              {isMobile ? 'Recent' : 'Recent Transactions'}
            </button>
            <button
              className={`tab-button ${activeTab === 'comparison' ? 'active' : ''}`}
              onClick={() => setActiveTab('comparison')}
              style={{
                fontSize: isMobile ? '11px' : '14px',
                padding: isMobile ? '8px 12px' : '12px 24px',
                flex: isMobile ? '1 1 calc(50% - 2px)' : 'none',
                minWidth: isMobile ? '0' : 'auto'
              }}
            >
              {isMobile ? 'Comparison' : 'Trading Comparison'}
            </button>
            <button
              className={`tab-button ${activeTab === 'volatility' ? 'active' : ''}`}
              onClick={() => setActiveTab('volatility')}
              style={{
                fontSize: isMobile ? '11px' : '14px',
                padding: isMobile ? '8px 12px' : '12px 24px',
                flex: isMobile ? '1 1 calc(50% - 2px)' : 'none',
                minWidth: isMobile ? '0' : 'auto'
              }}
            >
              {isMobile ? 'Volatility' : 'Volatility Data'}
            </button>
            <button
              className={`tab-button ${activeTab === 'reconciliation' ? 'active' : ''}`}
              onClick={() => setActiveTab('reconciliation')}
              style={{
                fontSize: isMobile ? '11px' : '14px',
                padding: isMobile ? '8px 12px' : '12px 24px',
                flex: isMobile ? '1 1 calc(50% - 2px)' : 'none',
                minWidth: isMobile ? '0' : 'auto'
              }}
            >
              {isMobile ? 'Reconcile' : 'Reconciliation'}
            </button>
            <button
              className={`tab-button ${activeTab === 'live-data' ? 'active' : ''}`}
              onClick={() => setActiveTab('live-data')}
              style={{
                fontSize: isMobile ? '11px' : '14px',
                padding: isMobile ? '8px 12px' : '12px 24px',
                flex: isMobile ? '1 1 calc(50% - 2px)' : 'none',
                minWidth: isMobile ? '0' : 'auto'
              }}
            >
              {isMobile ? 'Live' : 'Live Data'}
            </button>
          </div>

          {/* Refresh Button - Hide for reconciliation tab */}
          {(activeTab as string) !== 'reconciliation' && (activeTab as string) !== 'live-data' && (
            <div style={{ 
              marginBottom: isMobile ? '12px' : '16px',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: '12px'
            }}>
              <button
                onClick={() => loadData()}
                disabled={loading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: isMobile ? '8px 16px' : '10px 20px',
                  backgroundColor: loading ? '#e5e7eb' : '#086afb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: isMobile ? '12px' : '14px',
                  fontWeight: 500,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: loading ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.backgroundColor = '#0557d1';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.currentTarget.style.backgroundColor = '#086afb';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }
                }}
              >
                <svg 
                  width={isMobile ? '14' : '16'} 
                  height={isMobile ? '14' : '16'} 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                  style={{
                    animation: loading ? 'spin 1s linear infinite' : 'none'
                  }}
                >
                  <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
                </svg>
                <span>{loading ? 'Refreshing...' : 'Refresh Data'}</span>
              </button>
            </div>
          )}

          {/* Filters Section - Hide for reconciliation tab */}
          {(activeTab as string) !== 'reconciliation' && (activeTab as string) !== 'live-data' && (
            <div style={{ 
              marginBottom: isMobile ? '12px' : '16px', 
              display: 'flex', 
              alignItems: 'center', 
            gap: '12px',
            flexWrap: 'wrap',
            padding: isMobile ? '8px' : '12px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            {/* Market Type Filter (for Volatility tab) */}
            {activeTab === 'volatility' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: isMobile ? '1 1 100%' : '0 0 auto' }}>
                <label style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: 500, whiteSpace: 'nowrap' }}>
                  Market Type:
                </label>
                <select
                  value={marketType}
                  onChange={(e) => {
                    setMarketType(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: isMobile ? '6px 8px' : '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: isMobile ? '12px' : '14px',
                    minWidth: isMobile ? '120px' : '150px',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                >
                  <option value="FUTURES">Futures</option>
                  <option value="SPOT">Spot</option>
                </select>
              </div>
            )}
            {/* Strategy Name Filter */}
            {uniqueStrategyNames.length > 0 && activeTab !== 'volatility' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: isMobile ? '1 1 100%' : '0 0 auto' }}>
                <label style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: 500, whiteSpace: 'nowrap' }}>
                  Strategy:
                </label>
                <select
                  value={strategyFilter}
                  onChange={(e) => {
                    setStrategyFilter(e.target.value);
                    setCurrentPage(1); // Reset to first page when filter changes
                  }}
                  style={{
                    padding: isMobile ? '6px 8px' : '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: isMobile ? '12px' : '14px',
                    minWidth: isMobile ? '150px' : '200px',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                >
                  <option value="all">All Strategies</option>
                  {uniqueStrategyNames.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Days selector for Recent Transactions */}
            {activeTab === 'recent' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: isMobile ? '1 1 100%' : '0 0 auto' }}>
                <label style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: 500, whiteSpace: 'nowrap' }}>Days:</label>
              <select
                value={recentDays}
                onChange={(e) => {
                  setRecentDays(Number(e.target.value));
                  loadData();
                }}
                style={{
                    padding: isMobile ? '6px 8px' : '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: isMobile ? '12px' : '14px',
                    minWidth: isMobile ? '120px' : '150px',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                }}
              >
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={60}>Last 60 days</option>
                <option value={90}>Last 90 days</option>
              </select>
            </div>
          )}

            {/* Filter Results Count */}
            {strategyFilter !== 'all' && (
              <div style={{ 
                fontSize: isMobile ? '11px' : '12px', 
                color: '#6b7280',
                fontStyle: 'italic',
                marginLeft: 'auto'
              }}>
                Showing {filteredData.length} of {data.length} records
              </div>
            )}
          </div>
          )}

          {/* Export Buttons - Hide for reconciliation tab */}
          {(activeTab as string) !== 'reconciliation' && (activeTab as string) !== 'live-data' && (
          <div className="reports-actions" style={{ 
            display: 'flex', 
            gap: isMobile ? '6px' : '8px', 
            marginBottom: isMobile ? '12px' : '16px',
            justifyContent: isMobile ? 'stretch' : 'flex-end',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => {
                const exportFunc = activeTab === 'summary' 
                  ? () => adminApiService.exportTradingStrategySummaryCSV()
                  : activeTab === 'detailed'
                  ? () => adminApiService.exportTradingStrategyDetailedTransactionsCSV()
                  : activeTab === 'detailed-users'
                  ? () => adminApiService.exportTradingStrategyDetailedTransactionsWithUsersCSV()
                  : activeTab === 'pnl'
                  ? () => adminApiService.exportTradingStrategyPnlSummaryCSV()
                  : activeTab === 'recent'
                  ? () => adminApiService.exportTradingStrategyRecentTransactionsCSV(recentDays)
                  : () => adminApiService.exportTradingStrategyComparisonCSV();
                handleExportCSV(exportFunc, getExportFilename('csv'));
              }}
              disabled={loading || data.length === 0 || exporting !== null}
              className="btn btn-secondary"
              style={{ 
                fontSize: isMobile ? '12px' : '14px', 
                padding: isMobile ? '8px 12px' : '8px 16px',
                flex: isMobile ? '1 1 calc(50% - 3px)' : 'none',
                minWidth: isMobile ? '0' : 'auto'
              }}
            >
              {exporting === `csv-${activeTab}` ? 'Exporting...' : isMobile ? 'CSV' : 'Export CSV'}
            </button>
            <button
              onClick={() => {
                const exportFunc = activeTab === 'summary' 
                  ? () => adminApiService.exportTradingStrategySummaryPDF()
                  : activeTab === 'detailed'
                  ? () => adminApiService.exportTradingStrategyDetailedTransactionsPDF()
                  : activeTab === 'detailed-users'
                  ? () => adminApiService.exportTradingStrategyDetailedTransactionsWithUsersPDF()
                  : activeTab === 'pnl'
                  ? () => adminApiService.exportTradingStrategyPnlSummaryPDF()
                  : activeTab === 'recent'
                  ? () => adminApiService.exportTradingStrategyRecentTransactionsPDF(recentDays)
                  : () => adminApiService.exportTradingStrategyComparisonPDF();
                handleExportPDF(exportFunc, getExportFilename('pdf'));
              }}
              disabled={loading || data.length === 0 || exporting !== null}
              className="btn btn-secondary"
              style={{ 
                fontSize: isMobile ? '12px' : '14px', 
                padding: isMobile ? '8px 12px' : '8px 16px',
                flex: isMobile ? '1 1 calc(50% - 3px)' : 'none',
                minWidth: isMobile ? '0' : 'auto'
              }}
            >
              {exporting === `pdf-${activeTab}` ? 'Exporting...' : isMobile ? 'PDF' : 'Export PDF'}
            </button>
          </div>
          )}

          {/* Error Display - Hide for reconciliation tab */}
          {activeTab !== 'reconciliation' && error && (
            <div className="alert alert-error" style={{ marginBottom: '16px' }}>
              {error}
            </div>
          )}

          {/* Loading State - Hide for reconciliation tab */}
          {(activeTab as string) !== 'reconciliation' && (activeTab as string) !== 'live-data' && (
            <>
              {loading ? (
                <div className="loading" style={{ textAlign: 'center', padding: '40px' }}>
                  Loading data...
                </div>
              ) : data.length === 0 ? (
                <div className="empty-state" style={{ textAlign: 'center', padding: '40px' }}>
                  No data available
                </div>
              ) : (
                <>
                  {activeTab === 'volatility' ? (
                // Volatility Data Table
                <div style={{ marginBottom: '16px' }}>
                  {/* Summary Statistics */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '12px',
                    marginBottom: '16px',
                    padding: '16px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    {(() => {
                      const validVolatilities = filteredData
                        .map((row: any) => row.volatilityAuto)
                        .filter((v: any) => v != null && typeof v === 'number' && !isNaN(v));
                      const avgVol = validVolatilities.length > 0
                        ? validVolatilities.reduce((a: number, b: number) => a + b, 0) / validVolatilities.length
                        : 0;
                      const maxVol = validVolatilities.length > 0 ? Math.max(...validVolatilities) : 0;
                      const minVol = validVolatilities.length > 0 ? Math.min(...validVolatilities) : 0;
                      const totalVolume = filteredData.reduce((sum: number, row: any) => 
                        sum + (typeof row.volume24h === 'number' ? row.volume24h : 0), 0);
                      
                      return (
                        <>
                          <div>
                            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Average Volatility</div>
                            <div style={{ fontSize: '18px', fontWeight: 600, color: '#111827' }}>
                              {avgVol.toFixed(2)}%
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Highest Volatility</div>
                            <div style={{ fontSize: '18px', fontWeight: 600, color: '#dc2626' }}>
                              {maxVol.toFixed(2)}%
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Lowest Volatility</div>
                            <div style={{ fontSize: '18px', fontWeight: 600, color: '#16a34a' }}>
                              {minVol.toFixed(2)}%
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Total Market Volume</div>
                            <div style={{ fontSize: '18px', fontWeight: 600, color: '#111827' }}>
                              ${(totalVolume / 1000000).toFixed(2)}M
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  <div className="table-container" style={{ 
                    maxHeight: isMobile ? 'calc(100vh - 450px)' : '55vh', 
                    overflow: 'auto',
                    overflowX: 'auto',
                    overflowY: 'auto',
                    width: '100%',
                    WebkitOverflowScrolling: 'touch',
                    position: 'relative'
                  }}>
                    <table className="admin-table" style={{ 
                      width: '100%',
                      minWidth: isMobile ? '1000px' : '100%',
                      tableLayout: 'auto',
                      fontSize: isMobile ? '10px' : '11px'
                    }}>
                      <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 10 }}>
                        <tr>
                          <th onClick={() => handleSort('rank')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                            Rank {sortColumn === 'rank' && (sortDirection === 'asc' ? '↑' : '↓')}
                          </th>
                          <th onClick={() => handleSort('symbol')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                            Symbol {sortColumn === 'symbol' && (sortDirection === 'asc' ? '↑' : '↓')}
                          </th>
                          <th onClick={() => handleSort('coinName')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                            Coin {sortColumn === 'coinName' && (sortDirection === 'asc' ? '↑' : '↓')}
                          </th>
                          <th onClick={() => handleSort('price')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                            Price {sortColumn === 'price' && (sortDirection === 'asc' ? '↑' : '↓')}
                          </th>
                          <th onClick={() => handleSort('volume24h')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                            Volume 24h {sortColumn === 'volume24h' && (sortDirection === 'asc' ? '↑' : '↓')}
                          </th>
                          <th onClick={() => handleSort('volatilityAuto')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                            Volatility {sortColumn === 'volatilityAuto' && (sortDirection === 'asc' ? '↑' : '↓')}
                          </th>
                          <th onClick={() => handleSort('volatilityHighLow')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                            Vol (High/Low) {sortColumn === 'volatilityHighLow' && (sortDirection === 'asc' ? '↑' : '↓')}
                          </th>
                          <th onClick={() => handleSort('volatilityChange24h')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                            Vol (24h Change) {sortColumn === 'volatilityChange24h' && (sortDirection === 'asc' ? '↑' : '↓')}
                          </th>
                          <th onClick={() => handleSort('priceRange')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                            Price Range {sortColumn === 'priceRange' && (sortDirection === 'asc' ? '↑' : '↓')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedData.map((row: any, index: number) => {
                          const volatility = row.volatilityAuto || 0;
                          const volatilityColor = volatility < 5 ? '#16a34a' : volatility < 10 ? '#eab308' : volatility < 15 ? '#f97316' : '#dc2626';
                          
                          return (
                            <tr key={index} style={{ backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb' }}>
                              <td style={{ textAlign: 'center', fontWeight: 500 }}>{row.rank || '-'}</td>
                              <td style={{ fontFamily: 'monospace', fontSize: '10px' }}>{row.symbol || '-'}</td>
                              <td style={{ fontWeight: 500 }}>{row.coinName || '-'}</td>
                              <td style={{ textAlign: 'right' }}>
                                {row.price ? `$${parseFloat(row.price).toFixed(8).replace(/\.?0+$/, '')}` : '-'}
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                {row.volume24h ? `$${(parseFloat(row.volume24h) / 1000000).toFixed(2)}M` : '-'}
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: 600, color: volatilityColor }}>
                                {volatility ? `${parseFloat(volatility).toFixed(2)}%` : '-'}
                              </td>
                              <td style={{ textAlign: 'right', color: '#6b7280' }}>
                                {row.volatilityHighLow ? `${parseFloat(row.volatilityHighLow).toFixed(2)}%` : '-'}
                              </td>
                              <td style={{ textAlign: 'right', color: '#6b7280' }}>
                                {row.volatilityChange24h ? `${parseFloat(row.volatilityChange24h).toFixed(2)}%` : '-'}
                              </td>
                              <td style={{ textAlign: 'right', color: '#6b7280' }}>
                                {row.priceRange ? `$${parseFloat(row.priceRange).toFixed(8).replace(/\.?0+$/, '')}` : '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                // Regular Table for other tabs (including Detailed Transactions)
                <>
                <div className="table-container" style={{ 
                maxHeight: isMobile ? 'calc(100vh - 350px)' : '55vh', 
                overflow: 'auto',
                overflowX: 'auto',
                overflowY: 'auto',
                width: '100%',
                WebkitOverflowScrolling: 'touch',
                position: 'relative'
              }}>
                <table className="admin-table" style={{ 
                  width: '100%',
                  minWidth: isMobile ? '800px' : '100%',
                  tableLayout: 'auto',
                  fontSize: isMobile ? '10px' : '11px'
                }}>
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 10 }}>
                    <tr>
                      {getTableHeaders().map((header) => (
                        <th 
                          key={header} 
                          onClick={() => handleSort(header)}
                          style={{ 
                            padding: isMobile ? '4px 6px' : '6px 8px', 
                            textAlign: 'left', 
                            borderBottom: '2px solid #dee2e6',
                            whiteSpace: 'nowrap',
                            fontSize: isMobile ? '10px' : '11px',
                            fontWeight: 600,
                            backgroundColor: sortColumn === header ? '#e0e7ff' : '#f8f9fa',
                            cursor: 'pointer',
                            userSelect: 'none',
                            position: 'relative',
                            paddingRight: '20px'
                          }}
                          title="Click to sort"
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span>{header.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                            {sortColumn === header && (
                              <span style={{ fontSize: '10px', color: '#3b82f6' }}>
                                {sortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                            {sortColumn !== header && (
                              <span style={{ fontSize: '10px', color: '#9ca3af', opacity: 0.5 }}>⇅</span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedData.map((row, index) => (
                      <tr key={startIndex + index}>
                        {getTableHeaders().map((header) => (
                          <td key={header} style={{ 
                            padding: isMobile ? '4px 6px' : '6px 8px', 
                            borderBottom: '1px solid #dee2e6',
                            whiteSpace: 'nowrap',
                            fontSize: isMobile ? '10px' : '11px'
                          }}>
                            {formatValue(row[header])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Detailed Transactions Entry/Exit Price Chart */}
              {activeTab === 'detailed' && detailedPriceChart && (
                <div
                  style={{
                    marginTop: isMobile ? '12px' : '16px',
                    height: isMobile ? '260px' : '320px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    padding: isMobile ? '8px' : '12px',
                    backgroundColor: 'white',
                  }}
                >
                  <Line data={detailedPriceChart.data} options={detailedPriceChart.options} />
                </div>
              )}
                </>
              )}
            </>
          )}

          {/* Pagination Controls - Hide for reconciliation tab */}
          {(activeTab as string) !== 'reconciliation' && data.length > 0 && (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: isMobile ? 'flex-start' : 'center',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '12px' : '0',
                marginTop: isMobile ? '12px' : '16px',
                padding: isMobile ? '8px' : '12px',
                borderTop: '1px solid #e5e7eb',
                backgroundColor: '#f9fafb'
              }}>
                  <div style={{ 
                    fontSize: isMobile ? '11px' : '12px', 
                    color: '#6b7280',
                    textAlign: isMobile ? 'center' : 'left',
                    width: isMobile ? '100%' : 'auto'
                  }}>
                    Showing {startIndex + 1} to {Math.min(endIndex, sortedData.length)} of {sortedData.length} records
                    {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
                    {sortColumn && (
                      <span style={{ marginLeft: '8px', fontStyle: 'italic' }}>
                        • Sorted by {sortColumn.replace(/_/g, ' ')} ({sortDirection === 'asc' ? 'ascending' : 'descending'})
                      </span>
                    )}
                  </div>
                {totalPages > 1 && (
                  <div style={{ 
                    display: 'flex', 
                    gap: isMobile ? '2px' : '4px', 
                    alignItems: 'center',
                    justifyContent: isMobile ? 'center' : 'flex-end',
                    width: isMobile ? '100%' : 'auto',
                    flexWrap: 'wrap'
                  }}>
                    <button
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      style={{
                        padding: isMobile ? '6px 10px' : '6px 12px',
                        fontSize: isMobile ? '11px' : '12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        backgroundColor: currentPage === 1 ? '#f3f4f6' : 'white',
                        color: currentPage === 1 ? '#9ca3af' : '#374151',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        fontWeight: 500
                      }}
                    >
                      {isMobile ? 'Prev' : 'Previous'}
                    </button>
                    <div style={{ display: 'flex', gap: '2px', flexWrap: 'wrap', justifyContent: 'center' }}>
                      {Array.from({ length: Math.min(isMobile ? 3 : 5, totalPages) }, (_, i) => {
                        let pageNum: number;
                        const maxButtons = isMobile ? 3 : 5;
                        if (totalPages <= maxButtons) {
                          pageNum = i + 1;
                        } else if (currentPage <= Math.ceil(maxButtons / 2)) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - Math.floor(maxButtons / 2)) {
                          pageNum = totalPages - maxButtons + i + 1;
                        } else {
                          pageNum = currentPage - Math.floor(maxButtons / 2) + i;
                        }
                        return (
                          <button
                            key={pageNum}
                            onClick={() => goToPage(pageNum)}
                            style={{
                              padding: isMobile ? '6px 8px' : '6px 10px',
                              fontSize: isMobile ? '11px' : '12px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              backgroundColor: currentPage === pageNum ? '#3b82f6' : 'white',
                              color: currentPage === pageNum ? 'white' : '#374151',
                              cursor: 'pointer',
                              fontWeight: currentPage === pageNum ? 600 : 400,
                              minWidth: isMobile ? '28px' : '32px'
                            }}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      style={{
                        padding: isMobile ? '6px 10px' : '6px 12px',
                        fontSize: isMobile ? '11px' : '12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        backgroundColor: currentPage === totalPages ? '#f3f4f6' : 'white',
                        color: currentPage === totalPages ? '#9ca3af' : '#374151',
                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                        fontWeight: 500
                      }}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
              )}

          {/* Subtotal Summary Section - Exclude from reconciliation tab */}
          {(activeTab as string) !== 'reconciliation' && sortedData.length > 0 && (() => {
                const { 
                  totalFees, 
                  totalPnl, 
                  unrealizedPnl, 
                  totalPnlWithUnrealized, 
                  netProfitLoss,
                  netProfitLossWithUnrealized,
                  activePositions
                } = calculateTotals();
                const hasFeesOrPnl = totalFees !== 0 || totalPnl !== 0 || unrealizedPnl !== 0;
                
                if (!hasFeesOrPnl) return null;

                return (
                  <div style={{
                    marginTop: '24px',
                    padding: isMobile ? '16px' : '20px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb'
                  }}>
                    <h3 style={{
                      fontSize: isMobile ? '14px' : '16px',
                      fontWeight: 600,
                      marginBottom: '16px',
                      color: '#374151',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span>📊</span>
                      <span>Complete P&L Summary</span>
                    </h3>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                      gap: isMobile ? '12px' : '16px',
                      marginBottom: '16px'
                    }}>
                      {/* Realized P&L (Closed Trades) */}
                      <div style={{
                        padding: isMobile ? '12px' : '16px',
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                      }}>
                        <div style={{
                          fontSize: isMobile ? '10px' : '11px',
                          color: '#6b7280',
                          fontWeight: 500,
                          marginBottom: '4px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <span>💰</span>
                          <span>Realized P&L</span>
                        </div>
                        <div style={{
                          fontSize: isMobile ? '20px' : '24px',
                          fontWeight: 700,
                          color: totalPnl >= 0 ? '#16a34a' : '#dc2626'
                        }}>
                          {formatCurrency(totalPnl)}
                        </div>
                        <div style={{
                          fontSize: isMobile ? '9px' : '10px',
                          color: '#6b7280',
                          marginTop: '4px',
                          fontStyle: 'italic'
                        }}>
                          From closed trades
                        </div>
                      </div>

                      {/* Unrealized P&L (Open Positions) */}
                      <div style={{
                        padding: isMobile ? '12px' : '16px',
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                      }}>
                        <div style={{
                          fontSize: isMobile ? '10px' : '11px',
                          color: '#6b7280',
                          fontWeight: 500,
                          marginBottom: '4px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <span>📈</span>
                          <span>Unrealized P&L</span>
                        </div>
                        <div style={{
                          fontSize: isMobile ? '20px' : '24px',
                          fontWeight: 700,
                          color: unrealizedPnl >= 0 ? '#16a34a' : '#dc2626'
                        }}>
                          {formatCurrency(unrealizedPnl)}
                        </div>
                        <div style={{
                          fontSize: isMobile ? '9px' : '10px',
                          color: '#6b7280',
                          marginTop: '4px',
                          fontStyle: 'italic'
                        }}>
                          From {activePositions} open position{activePositions !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                      gap: isMobile ? '12px' : '16px'
                    }}>
                      {/* Total Fees */}
                      <div style={{
                        padding: isMobile ? '12px' : '16px',
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                      }}>
                        <div style={{
                          fontSize: isMobile ? '10px' : '11px',
                          color: '#6b7280',
                          fontWeight: 500,
                          marginBottom: '4px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Total Fees Paid
                        </div>
                        <div style={{
                          fontSize: isMobile ? '20px' : '24px',
                          fontWeight: 700,
                          color: '#dc2626'
                        }}>
                          {formatCurrency(totalFees)}
                        </div>
                      </div>

                      {/* Total P&L (Realized + Unrealized) */}
                      <div style={{
                        padding: isMobile ? '12px' : '16px',
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                      }}>
                        <div style={{
                          fontSize: isMobile ? '10px' : '11px',
                          color: '#6b7280',
                          fontWeight: 500,
                          marginBottom: '4px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Total P&L
                        </div>
                        <div style={{
                          fontSize: isMobile ? '20px' : '24px',
                          fontWeight: 700,
                          color: totalPnlWithUnrealized >= 0 ? '#16a34a' : '#dc2626'
                        }}>
                          {formatCurrency(totalPnlWithUnrealized)}
                        </div>
                        <div style={{
                          fontSize: isMobile ? '9px' : '10px',
                          color: '#6b7280',
                          marginTop: '4px',
                          fontStyle: 'italic'
                        }}>
                          (Realized + Unrealized)
                        </div>
                      </div>

                      {/* Net Profit/Loss (Total P&L - Fees) */}
                      <div style={{
                        padding: isMobile ? '12px' : '16px',
                        backgroundColor: netProfitLossWithUnrealized >= 0 ? '#f0fdf4' : '#fef2f2',
                        borderRadius: '8px',
                        border: `2px solid ${netProfitLossWithUnrealized >= 0 ? '#16a34a' : '#dc2626'}`,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                      }}>
                        <div style={{
                          fontSize: isMobile ? '10px' : '11px',
                          color: '#6b7280',
                          fontWeight: 600,
                          marginBottom: '4px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Net Profit/Loss
                        </div>
                        <div style={{
                          fontSize: isMobile ? '20px' : '24px',
                          fontWeight: 700,
                          color: netProfitLossWithUnrealized >= 0 ? '#16a34a' : '#dc2626'
                        }}>
                          {formatCurrency(netProfitLossWithUnrealized)}
                        </div>
                        <div style={{
                          fontSize: isMobile ? '9px' : '10px',
                          color: '#6b7280',
                          marginTop: '4px',
                          fontStyle: 'italic'
                        }}>
                          (Total P&L - Fees)
                        </div>
                      </div>
                    </div>

                    {/* Additional info text */}
                    <div style={{
                      marginTop: '12px',
                      paddingTop: '12px',
                      borderTop: '1px solid #e5e7eb',
                      fontSize: isMobile ? '10px' : '11px',
                      color: '#6b7280',
                      textAlign: 'center',
                      fontStyle: 'italic'
                    }}>
                      Summary calculated from {sortedData.length} record{sortedData.length !== 1 ? 's' : ''}
                      {strategyFilter !== 'all' && ` for strategy: ${strategyFilter}`}
                    </div>

                    {/* P&L Explanation Toggle Button */}
                    <div style={{
                      marginTop: '16px',
                      display: 'flex',
                      justifyContent: 'center'
                    }}>
                      <button
                        onClick={() => setShowPnlExplanation(!showPnlExplanation)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: isMobile ? '8px 16px' : '10px 20px',
                          backgroundColor: 'transparent',
                          color: '#086afb',
                          border: '1px solid #086afb',
                          borderRadius: '8px',
                          fontSize: isMobile ? '12px' : '14px',
                          fontWeight: 500,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f0f7ff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <svg 
                          width="16" 
                          height="16" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2"
                          style={{
                            transform: showPnlExplanation ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease'
                          }}
                        >
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                        <span>{showPnlExplanation ? 'Hide' : 'Show'} P&L Breakdown</span>
                      </button>
                    </div>

                    {/* P&L Explanation Panel */}
                    {showPnlExplanation && (
                      <div style={{
                        marginTop: '16px',
                        padding: isMobile ? '16px' : '24px',
                        backgroundColor: '#f9fafb',
                        borderRadius: '12px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <h4 style={{
                          fontSize: isMobile ? '16px' : '18px',
                          fontWeight: 600,
                          color: '#111827',
                          marginBottom: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <span>💡</span>
                          <span>Understanding Your P&L</span>
                        </h4>

                        <div style={{
                          display: 'grid',
                          gap: '12px'
                        }}>
                          {/* Total P&L (Before Fees) */}
                          <div style={{
                            padding: '12px',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            borderLeft: '4px solid #086afb'
                          }}>
                            <div style={{ 
                              fontSize: isMobile ? '13px' : '14px',
                              fontWeight: 600,
                              color: '#374151',
                              marginBottom: '4px'
                            }}>
                              Total P&L (Before Fees): {formatCurrency(totalPnlWithUnrealized)}
                            </div>
                            <div style={{
                              fontSize: isMobile ? '11px' : '12px',
                              color: '#6b7280',
                              lineHeight: '1.5'
                            }}>
                              <div>• Realized P&L: {formatCurrency(totalPnl)} (from closed trades)</div>
                              <div>• Unrealized P&L: {formatCurrency(unrealizedPnl)} (from {activePositions} open position{activePositions !== 1 ? 's' : ''})</div>
                            </div>
                          </div>

                          {/* Fees */}
                          <div style={{
                            padding: '12px',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            borderLeft: '4px solid #f59e0b'
                          }}>
                            <div style={{ 
                              fontSize: isMobile ? '13px' : '14px',
                              fontWeight: 600,
                              color: '#374151',
                              marginBottom: '4px'
                            }}>
                              Total Trading Fees: {formatCurrency(totalFees)}
                            </div>
                            <div style={{
                              fontSize: isMobile ? '11px' : '12px',
                              color: '#6b7280'
                            }}>
                              Fees paid to the exchange (Kucoin)
                            </div>
                          </div>

                          {/* Net Profit/Loss */}
                          <div style={{
                            padding: '16px',
                            backgroundColor: netProfitLossWithUnrealized >= 0 ? '#dcfce7' : '#fee2e2',
                            borderRadius: '8px',
                            borderLeft: `4px solid ${netProfitLossWithUnrealized >= 0 ? '#16a34a' : '#dc2626'}`
                          }}>
                            <div style={{ 
                              fontSize: isMobile ? '14px' : '16px',
                              fontWeight: 700,
                              color: netProfitLossWithUnrealized >= 0 ? '#166534' : '#991b1b',
                              marginBottom: '8px'
                            }}>
                              💰 Your Actual Profit: {formatCurrency(netProfitLossWithUnrealized)}
                            </div>
                            <div style={{
                              fontSize: isMobile ? '11px' : '12px',
                              color: netProfitLossWithUnrealized >= 0 ? '#166534' : '#991b1b',
                              lineHeight: '1.6'
                            }}>
                              <div style={{ marginBottom: '8px' }}>
                                <strong>Calculation:</strong> {formatCurrency(totalPnlWithUnrealized)} (Total P&L) - {formatCurrency(totalFees)} (Fees) = {formatCurrency(netProfitLossWithUnrealized)}
                              </div>
                              <div style={{
                                padding: '8px',
                                backgroundColor: 'rgba(255,255,255,0.7)',
                                borderRadius: '6px',
                                marginTop: '8px'
                              }}>
                                <div><strong>What this means:</strong></div>
                                <div>• This is your <strong>real profit</strong> after all fees</div>
                                <div>• Includes locked-in profits from closed trades</div>
                                <div>• Includes current value of open positions</div>
                                <div>• All exchange fees are deducted</div>
                              </div>
                            </div>
                          </div>

                          {/* Account Value Example */}
                          {netProfitLossWithUnrealized !== 0 && (
                            <div style={{
                              padding: '12px',
                              backgroundColor: 'white',
                              borderRadius: '8px',
                              border: '1px solid #e5e7eb'
                            }}>
                              <div style={{
                                fontSize: isMobile ? '11px' : '12px',
                                color: '#6b7280',
                                lineHeight: '1.6'
                              }}>
                                <div style={{ 
                                  fontWeight: 600,
                                  color: '#374151',
                                  marginBottom: '4px'
                                }}>
                                  📊 Example:
                                </div>
                                <div>If you started with <strong>$100.00</strong></div>
                                <div>Your account would now be worth: <strong style={{ color: netProfitLossWithUnrealized >= 0 ? '#16a34a' : '#dc2626' }}>
                                  ${(100 + parseFloat(netProfitLossWithUnrealized.toString())).toFixed(2)}
                                </strong></div>
                                {netProfitLossWithUnrealized !== 0 && (
                                  <div>Return: <strong style={{ color: netProfitLossWithUnrealized >= 0 ? '#16a34a' : '#dc2626' }}>
                                    {((netProfitLossWithUnrealized / 100) * 100).toFixed(2)}%
                                  </strong></div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </>
          )}

          {/* Reconciliation Tab */}
          {(activeTab as string) === 'reconciliation' && (
            <div style={{ minHeight: '400px' }}>
              <ReconciliationPanel strategyId={selectedStrategyId} />
            </div>
          )}

          {(activeTab as string) === 'live-data' && (
            <div
              style={{
                minHeight: '400px',
                paddingBottom: isMobile ? '120px' : 0, // Extra space so bottom rows are visible above mobile nav
              }}
            >
              <LiveDataPanel marketType={marketType} />
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default TradingStrategyReportsModal;

